import { DateTime } from "luxon";
// Import existing calendar components from the original calendar domain
import { CalendarView } from "@/domains/appointment/calendar/components/calendar-view";
import { DragDropProvider } from "@/domains/appointment/calendar/components/interactions/drag-drop-context";
import { useCalendarAppointmentMutations } from "@/domains/appointment/calendar/hooks/use-calendar-appointment-mutations";
import type { CalendarViewType } from "@/domains/appointment/calendar/types";
import { useDialogRouterSimple as useDialogRouter } from "@/lib/dialog/use-dialog-router-simple";
import type { NewCalendarViewMode } from "../../types";

interface CalendarViewsProps {
  viewMode: NewCalendarViewMode;
  currentDate: DateTime;
}

// Map new calendar view modes to existing calendar view types
const mapViewMode = (viewMode: NewCalendarViewMode): CalendarViewType => {
  switch (viewMode) {
    case "daily":
      return "day";
    case "3-day":
      return "3-day";
    case "week":
      return "week";
    case "month":
      return "month";
    case "worker":
      return "worker";
    default:
      return "day";
  }
};

export function CalendarViews({ viewMode, currentDate }: CalendarViewsProps) {
  // Use existing calendar infrastructure
  const { openDialog } = useDialogRouter();
  const { updateAppointment } = useCalendarAppointmentMutations();

  const handleAppointmentClick = (appointmentId: string) => {
    openDialog({ type: "appointment-detail", id: appointmentId });
  };

  const handleAppointmentDrop = async (appointmentId: string, newDate: DateTime) => {
    await updateAppointment(appointmentId, {
      startTime: newDate.toISO(),
    });
  };

  const _handleShowCreateDialog = (_date?: DateTime) => {
    // Create appointment (with or without specific time)
    openDialog({ type: "appointment-create" });
  };

  const calendarViewType = mapViewMode(viewMode);

  return (
    <DragDropProvider>
      <CalendarView
        viewType={calendarViewType}
        currentDate={currentDate}
        onAppointmentClick={handleAppointmentClick}
        onAppointmentDrop={handleAppointmentDrop}
      />
    </DragDropProvider>
  );
}
